import moment from 'moment';
import { seedQuantitySchedule } from './quantityMatch';

// Pure data shaping for the Weekly Update tab (Baseline & Weekly page). Kept
// out of the component so the seeding precedence and week conventions can be
// unit tested against plain API rows.

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

/**
 * The week-ending date a save defaults to: the next Saturday on or after
 * `todayIso`. Jobs mostly run six-day Mon–Sat weeks (workDays defaults to 6
 * everywhere in the quantity schedule), so Saturday closes the week; a Sunday
 * save already belongs to the week ahead. Editable in the UI either way.
 */
export const defaultWeekEnding = (todayIso) => {
    const d = moment(todayIso, 'YYYY-MM-DD');
    if (!d.isValid()) return null;
    for (let i = 0; d.day() !== 6 && i < 7; i += 1) d.add(1, 'day');
    return d.format('YYYY-MM-DD');
};

/** The saved qtyschedule cost-code row → editable grid row, same mapping as
 *  the Quantity Schedule grid so a weekly re-save round-trips exactly. */
const fromSaved = (r, i) => ({
    costCode: String(r.Cost_Code ?? ''),
    description: r.Description ?? '',
    uom: r.UOM ?? '',
    qty: num(r.Budget_Qty),
    budgetHours: r.Budget_Hours == null ? null : num(r.Budget_Hours),
    // SQL DATE columns serialize as UTC midnights — read them as UTC or the
    // Central-time local parse walks every date back a day.
    startDate: r.Start_Date ? moment.utc(r.Start_Date).format('YYYY-MM-DD') : null,
    finishDate: r.Finish_Date ? moment.utc(r.Finish_Date).format('YYYY-MM-DD') : null,
    dailyRate: r.Daily_Rate == null ? null : num(r.Daily_Rate),
    curve: r.Curve || 'linear',
    workDays: r.Work_Days ? Number(r.Work_Days) : 6,
    activityCode: r.Activity_Code ?? null,
    // Carried through untouched: the weekly grid revises quantities but never
    // decides where they came from — that is the Baseline view's mapping.
    qtySource: r.Qty_Source ?? null,
    sortOrder: i + 1,
});

/**
 * Rows the weekly grid opens with, by precedence:
 *   1. the active quantity schedule's cost-code rows — last week's plan (or
 *      whatever another schedule page saved), ready to revise;
 *   2. failing that, a fresh seed from the baseline + schedule activities,
 *      exactly like the Quantity Schedule setup step;
 *   3. failing that, nothing — the Baseline tab still needs its uploads.
 */
export const seedWeeklyRows = (quantityRows, baselineRows, activities) => {
    const savedRows = (quantityRows || []).filter((r) => !r.Period_Date);
    if (savedRows.length > 0) {
        return { seededFrom: 'qtyschedule', rows: savedRows.map(fromSaved) };
    }
    const baseRows = (baselineRows || []).filter((r) => !r.Period_Date);
    if (baseRows.length > 0) {
        return { seededFrom: 'baseline', rows: seedQuantitySchedule(baseRows, activities || []) };
    }
    return { seededFrom: 'empty', rows: [] };
};

/**
 * The page's save history: every qtyschedule import for the job, newest first.
 * Saves from the Baseline view's mapping panel are kept in the list but
 * flagged — the PM should see when one of those displaced the weekly plan as
 * the active schedule.
 */
export const weeklyHistory = (imports) => (imports || [])
    .filter((i) => i.Kind === 'qtyschedule')
    .slice()
    .sort((a, b) => String(b.Uploaded_At || '').localeCompare(String(a.Uploaded_At || '')))
    .map((i) => ({
        importId: i.Import_ID,
        isWeekly: i.Source === 'weekly',
        weekEnding: i.Data_Date ? moment.utc(i.Data_Date).format('YYYY-MM-DD') : null,
        by: i.Uploaded_By || '',
        at: i.Uploaded_At || null,
        rows: num(i.Row_Count),
        active: i.Is_Active === true || i.Is_Active === 1,
        notes: i.Notes || '',
    }));

/** What still needs a human before this row produces a curve. */
export const gapOf = (r) => {
    if (!num(r.qty)) return 'needs quantity';
    if (!r.startDate) return 'needs start';
    if (!r.finishDate && !num(r.dailyRate)) return 'needs finish';
    if (r.finishDate && r.startDate && moment(r.finishDate).isBefore(moment(r.startDate))) return 'finish before start';
    return null;
};

/**
 * Fold the qty-history `history` recordset (per cost code + task) into one
 * installed-to-date number per cost code, for the grid's reference column.
 */
export const toDateByCode = (historyRows) => {
    const out = {};
    (historyRows || []).forEach((h) => {
        const code = String(h.CostCode || '').trim();
        if (!code) return;
        out[code] = (out[code] || 0) + num(h.ToDate);
    });
    return out;
};
